<template>
  <fieldset :id="props.id" class="dob-fieldset">
    <legend v-if="props.label" class="u-spaceBottom04">
      {{ props.label }}
      <!-- Required field indicator with asterisk -->
      <span v-if="props.required"
        ><span class="u-hiddenVisually">(required)</span><sup class="u-textRed">*</sup></span
      >
      <!-- Optional field indicator for screen readers -->
      <span v-else class="u-hiddenVisually">(optional)</span>
    </legend>
    <div class="Grid Grid--withGutter">
      <div class="Grid-cell u-size1of2">
        <BaseSelect
          v-model="month"
          :id="'providerDobMonth' + index"
          :name="'providerDobMonth' + index"
          label="Month"
          label-class="u-hiddenVisually"
          :options="months"
          option-value="value"
          option-label="name"
          placeholder="Month"
          :select-class="groupErrorMessage && !componentValidation.month ? 'u-borderColorRed' : ''"
          autocomplete="bday-month"
        />
      </div>
      <div class="Grid-cell u-size1of4">
        <BaseInput
          v-model="day"
          :id="'providerDobDay' + index"
          :name="'providerDobDay' + index"
          label="Day in two digits"
          label-class="u-hiddenVisually"
          placeholder="01"
          type="text"
          mask="##"
          :hide-error-message="true"
          :input-class="groupErrorMessage && !componentValidation.day ? 'u-borderColorRed' : ''"
          autocomplete="bday-day"
        />
      </div>
      <div class="Grid-cell u-size1of4">
        <BaseInput
          v-model="year"
          :id="'providerDobYear' + index"
          :name="'providerDobYear' + index"
          label="Year in four digits"
          label-class="u-hiddenVisually"
          placeholder="1990"
          type="text"
          mask="####"
          :hide-error-message="true"
          :input-class="groupErrorMessage && !componentValidation.year ? 'u-borderColorRed' : ''"
          autocomplete="bday-year"
        />
      </div>
    </div>
    <!-- Help text for additional context -->
    <small class="u-textShrink2 u-textBlackAlt">E.g. January 01, 1990</small>
    <!-- Group validation error message -->
    <p
      class="dob-errors u-textShrink1 u-textRed u-spaceTop02"
      v-if="groupErrorMessage"
      role="alert"
    >
      {{ groupErrorMessage }}
    </p>
  </fieldset>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue'
import { useField } from 'vee-validate'
import months from '@/data/months.json'

import BaseSelect from './BaseSelect.vue'
import BaseInput from './BaseInput.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: 'Date of Birth',
  },
  helpText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

// Internal state for the three date components
const month = ref('')
const day = ref('')
const year = ref('')

// Individual field validation functions - now context-aware
const validateMonth = (monthValue, showRequiredErrors = false) => {
  if (!monthValue || monthValue === '') {
    if (showRequiredErrors) {
      return { isValid: false, message: 'Month is required' }
    }
    return { isValid: true, message: '' } // Don't show required error during typing
  }
  const monthNum = parseInt(monthValue, 10)
  if (monthNum < 1 || monthNum > 12) {
    return { isValid: false, message: 'Please select a valid month' }
  }
  return { isValid: true, message: '' }
}

const validateDay = (dayValue, monthValue, yearValue, showRequiredErrors = false) => {
  if (!dayValue || dayValue === '') {
    if (showRequiredErrors) {
      return { isValid: false, message: 'Day is required' }
    }
    return { isValid: true, message: '' } // Don't show required error during typing
  }
  const dayNum = parseInt(dayValue, 10)
  if (dayNum < 1) {
    return { isValid: false, message: 'Day must be at least 1' }
  }

  // If we have month and year, validate against actual days in month
  if (monthValue && yearValue) {
    const monthNum = parseInt(monthValue, 10)
    const yearNum = parseInt(yearValue, 10)
    if (monthNum >= 1 && monthNum <= 12 && yearNum >= 1940 && yearNum <= 2029) {
      const daysInMonth = new Date(yearNum, monthNum, 0).getDate()
      if (dayNum > daysInMonth) {
        const monthName =
          months.find((mo) => mo.value === monthValue.padStart(2, '0'))?.name ||
          'the selected month'
        return { isValid: false, message: `${monthName} only has ${daysInMonth} days` }
      }
    }
  } else if (dayNum > 31) {
    return { isValid: false, message: 'Day cannot be greater than 31' }
  }

  return { isValid: true, message: '' }
}

const validateYear = (yearValue, showRequiredErrors = false) => {
  if (!yearValue || yearValue === '') {
    if (showRequiredErrors) {
      return { isValid: false, message: 'Year is required' }
    }
    return { isValid: true, message: '' } // Don't show required error during typing
  }
  const yearNum = parseInt(yearValue, 10)
  if (yearNum < 1940) {
    return { isValid: false, message: 'Year must be 1940 or later' }
  }
  if (yearNum > 2029) {
    return { isValid: false, message: 'Year cannot be later than 2029' }
  }

  // Check if year is in the future relative to today
  const today = new Date()
  const currentYear = today.getFullYear()
  if (yearNum > currentYear) {
    return { isValid: false, message: 'Birth year cannot be in the future' }
  }

  // Check for reasonable age (not more than 150 years old)
  const age = currentYear - yearNum
  if (age > 150) {
    return { isValid: false, message: 'Please enter a more recent birth year' }
  }

  return { isValid: true, message: '' }
}

const validateCompleteDate = (monthValue, dayValue, yearValue) => {
  if (!monthValue || !dayValue || !yearValue) {
    return { isValid: true, message: '' } // Individual field validation will handle this
  }

  const monthNum = parseInt(monthValue, 10)
  const dayNum = parseInt(dayValue, 10)
  const yearNum = parseInt(yearValue, 10)

  // Create the date and validate it's not in the future
  const inputDate = new Date(yearNum, monthNum - 1, dayNum)
  const today = new Date()

  if (inputDate > today) {
    return { isValid: false, message: 'Date of birth cannot be in the future' }
  }

  return { isValid: true, message: '' }
}

// Group validation rules
const dateValidationRules = computed(() => {
  const rules = []

  // Main validation rule that checks all fields and returns specific messages
  rules.push((value) => {
    // Handle required validation first - only show if completely empty
    if (props.required && (!value || value === '')) {
      return 'Date of birth is required'
    }

    if (!value) return true // If not required and empty, it's valid

    // Parse the date components from the value
    const { month: m, day: d, year: y } = parseDate(value)

    // Determine if we should show required errors
    // Only show "required" errors if all fields are empty OR if we have a complete date attempt
    const hasAnyValue = m || d || y
    const hasAllValues = m && d && y
    const showRequiredErrors = !hasAnyValue || (hasAnyValue && props.required)

    // Get individual validation results
    const monthValidation = validateMonth(m, showRequiredErrors)
    const dayValidation = validateDay(d, m, y, showRequiredErrors)
    const yearValidation = validateYear(y, showRequiredErrors)
    const completeDateValidation = validateCompleteDate(m, d, y)

    // Collect all error messages, but prioritize non-required errors
    const requiredErrors = []
    const validationErrors = []

    if (!monthValidation.isValid) {
      if (monthValidation.message.includes('required')) {
        requiredErrors.push(monthValidation.message)
      } else {
        validationErrors.push(monthValidation.message)
      }
    }

    if (!dayValidation.isValid) {
      if (dayValidation.message.includes('required')) {
        requiredErrors.push(dayValidation.message)
      } else {
        validationErrors.push(dayValidation.message)
      }
    }

    if (!yearValidation.isValid) {
      if (yearValidation.message.includes('required')) {
        requiredErrors.push(yearValidation.message)
      } else {
        validationErrors.push(yearValidation.message)
      }
    }

    if (!completeDateValidation.isValid) {
      validationErrors.push(completeDateValidation.message)
    }

    // Prioritize validation errors over required errors
    // If we have validation errors, show those first
    if (validationErrors.length > 0) {
      return validationErrors.join('. ')
    }

    // If we only have required errors and the user has started typing,
    // show a more helpful message
    if (requiredErrors.length > 0) {
      if (hasAnyValue && !hasAllValues) {
        return 'Please complete all date fields'
      }
      return requiredErrors.join('. ')
    }

    return true
  })

  return rules
})

// Vee-validate integration for group validation
const {
  errorMessage: groupErrorMessage,
  value: fieldValue,
  handleChange,
  validate,
} = useField(props.name || props.id, dateValidationRules, {
  initialValue: props.modelValue || '',
  validateOnValueUpdate: true,
  validateOnBlur: true,
})

// Parse initial modelValue into separate components
const parseDate = (dateString) => {
  if (!dateString) return { month: '', day: '', year: '' }

  // Try to parse different date formats
  // Assuming format could be MM/DD/YYYY, YYYY-MM-DD, or MM-DD-YYYY
  const formats = [
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
    /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // MM-DD-YYYY
  ]

  for (let i = 0; i < formats.length; i++) {
    const match = dateString.match(formats[i])
    if (match) {
      if (i === 0 || i === 2) {
        // MM/DD/YYYY or MM-DD-YYYY
        return {
          month: match[1].padStart(2, '0'),
          day: match[2].padStart(2, '0'),
          year: match[3],
        }
      }
      if (i === 1) {
        // YYYY-MM-DD
        return {
          month: match[2].padStart(2, '0'),
          day: match[3].padStart(2, '0'),
          year: match[1],
        }
      }
    }
  }

  return { month: '', day: '', year: '' }
}

// Initialize the date components from modelValue
const initializeDateComponents = () => {
  const { month: m, day: d, year: y } = parseDate(props.modelValue)
  month.value = m
  day.value = d
  year.value = y
}

// Combined date value
const combinedDate = computed(() => {
  if (!month.value || !day.value || !year.value) {
    return ''
  }
  // Return in MM/DD/YYYY format
  return `${month.value.padStart(2, '0')}/${day.value.padStart(2, '0')}/${year.value}`
})

// Individual component validation status (for styling and detailed feedback)
const componentValidation = computed(() => {
  // Don't show required errors during typing unless all fields are attempted
  const hasAnyValue = month.value || day.value || year.value
  const hasAllValues = month.value && day.value && year.value
  const showRequiredErrors = !hasAnyValue || (hasAnyValue && props.required && !hasAllValues)

  const monthValidation = validateMonth(month.value, false) // Never show required in component validation
  const dayValidation = validateDay(day.value, month.value, year.value, false)
  const yearValidation = validateYear(year.value, false)

  return {
    month: monthValidation.isValid,
    day: dayValidation.isValid,
    year: yearValidation.isValid,
    messages: {
      month: monthValidation.message,
      day: dayValidation.message,
      year: yearValidation.message,
    },
  }
})

// Watch for changes in individual components and emit combined date
watch(
  [month, day, year],
  async () => {
    const newValue = combinedDate.value
    console.log('DateOfBirthGroup - Date components changed:', {
      month: month.value,
      day: day.value,
      year: year.value,
      combined: newValue,
      validation: componentValidation.value,
    })

    // Update vee-validate field value
    handleChange(newValue)

    // Emit to parent component
    emit('update:modelValue', newValue)

    // Validate the complete date
    await validate()
  },
  { deep: true },
)

// Watch for changes in modelValue prop and update components
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== combinedDate.value && newValue !== fieldValue.value) {
      handleChange(newValue)
      initializeDateComponents()
    }
  },
)

// Initialize on mount
initializeDateComponents()
</script>

<style scoped>
.dob-fieldset .input-error {
  display: none;
}
</style>
